import React, { useState, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useRecordsPage, useMessageChannelAdapter, useBreadcrumbRouteMetaEmitter } from 'hooks';
import { useFreshdeskContextState } from './freshdesk-context';
import { recordPageController } from 'controllers';

const initialContextValue = {
  loading: true,
  entity: null,
  associatedEntities: [],
  record: null,
  error: null,
  recordsListData: {},
  nativeDropdownChoicesByFieldId: new Map(),
};

export const RecordPageStateContext = React.createContext(initialContextValue);
const RecordPageUpdaterContext = React.createContext();

const RecordPageProvider = props => {
  const { state } = useLocation();
  const { fetchRecordAndEntityIfNull, fetchRecord: getRecord } = useRecordsPage();
  const { isFeatureEnabled } = useFreshdeskContextState();
  const { getNativeDropdownChoices } = useMessageChannelAdapter();
  const useDropdownLookup = isFeatureEnabled('useDropdownLookup');
  // The actual native-dropdown-choices API call is made by the host application over the message channel
  // (see useMessageChannelAdapter) - recordPageController only owns building filter params and mapping the
  // response, so it's handed the host-backed fetcher here rather than calling a REST service itself.
  recordPageController.nativeDropdownDataSource = { getNativeDropdownChoices };
  const { entity, associatedEntities, data: recordsListData } = state ?? { entity: null, associatedEntities: [] };
  const [recordPage, setRecordPage] = useState({ ...initialContextValue, entity, associatedEntities, recordsListData });
  const { displayId } = useParams();
  useBreadcrumbRouteMetaEmitter({
    entity: recordPage.entity,
    record: recordPage.record,
    displayId,
  });

  const fetchRecord = useCallback(async () => {
    setRecordPage(prev => {
      return {
        ...prev,
        loading: true,
      };
    });
    const recordData = await getRecord(recordPage.entity);
    setRecordPage(prev => {
      return {
        ...prev,
        ...recordData,
        loading: false,
      };
    });
  }, [recordPage.entity, getRecord]);

  const loadRecordPage = useCallback(
    async isCancelled => {
      setRecordPage(prev => {
        return {
          ...prev,
          loading: true,
        };
      });

      try {
        const entityRecordData = await fetchRecordAndEntityIfNull(entity);
        if (isCancelled()) return;
        const resolvedEntity = entityRecordData?.entity ?? entity;
        let nativeDropdownChoicesByFieldId = new Map();

        if (useDropdownLookup && resolvedEntity?.fields) {
          try {
            const choicesResponse = await recordPageController.fetchNativeDropdownChoices(resolvedEntity.fields);
            if (isCancelled()) return;
            if (choicesResponse?.success) {
              nativeDropdownChoicesByFieldId = choicesResponse.response;
            }
          } catch (error) {
            console.error('Error fetching native dropdown choices - ' + error);
          }
        }

        setRecordPage(prev => ({
          ...prev,
          ...entityRecordData,
          nativeDropdownChoicesByFieldId,
          loading: false,
        }));
      } catch (error) {
        if (isCancelled) return;
        console.error('Error loading record page - ' + error);
        setRecordPage(prev => ({
          ...prev,
          error,
          loading: false,
        }));
      }
    },
    [entity, fetchRecordAndEntityIfNull, useDropdownLookup]
  );

  useEffect(() => {
    let cancelled = false;
    loadRecordPage(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [loadRecordPage]);

  const updateFunctions = useMemo(() => {
    return {
      fetchRecord,
      setRecordPage,
    };
  }, [fetchRecord, setRecordPage]);

  return (
    <RecordPageStateContext.Provider value={recordPage}>
      <RecordPageUpdaterContext.Provider value={updateFunctions}>{props.children}</RecordPageUpdaterContext.Provider>
    </RecordPageStateContext.Provider>
  );
};

const useRecordPageState = () => {
  const recordPageState = useContext(RecordPageStateContext);
  if (!recordPageState) {
    throw new Error('useRecordPageState must be used within a RecordPageProvider');
  }
  return recordPageState;
};

const useRecordPageUpdater = () => {
  const { setRecordPage, fetchRecord } = useContext(RecordPageUpdaterContext);
  if (!setRecordPage || !fetchRecord) {
    throw new Error('useRecordPageUpdater must be used within a RecordPageProvider');
  }

  return { setRecordPage, fetchRecord };
};

export { RecordPageProvider, useRecordPageState, useRecordPageUpdater };
